import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { AlertCircle, Loader2, PauseCircle, Play, TrendingDown, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from 'components/context/AuthContext';
import { ModalsContext } from 'components/context/ModalsContext';
import GenericModal, { NON_DISMISSIBLE_MODAL_PROPS } from 'components/modals/GenericModal';
import { useAds } from 'components/context/AdsContext';
import metaApi from 'api/meta';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';

const card =
  'backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl shadow-lg p-4 sm:p-6 text-white/90';

// Polling del job de creative-ranking (kickoff 202 → /api/job-status/<id>).
const RANKING_POLL_INTERVAL_MS = 3000; // poll cada 3s
const RANKING_MAX_POLL_ATTEMPTS = 40; // ~2 min de safety timeout (40 * 3s)

const LABEL_UI = {
  ESTRELLA: {
    label: 'Estrella',
    pillClass:
      'border-amber-400/35 bg-amber-500/15 text-amber-100',
    description:
      'Está por encima del promedio y absorbe una parte relevante del gasto. Conviene sostener y escalar con cuidado.',
  },
  ESCALANDO: {
    label: 'Escalando',
    pillClass:
      'border-emerald-400/30 bg-emerald-500/12 text-emerald-100',
    description:
      'Viene mejorando y tiene señal positiva en el KPI. Es buen candidato para darle más oportunidades.',
  },
  BUEN_RENDIMIENTO: {
    label: 'Buen rendimiento',
    pillClass: 'border-emerald-400/30 bg-emerald-500/12 text-emerald-100',
    description:
      'Está rindiendo bien para el objetivo seleccionado. Conviene sostener y considerar escalar si mantiene la performance.',
  },
  CON_POTENCIAL: {
    label: 'Con potencial',
    pillClass:
      'border-green-400/30 bg-green-500/12 text-green-100',
    description:
      'Aún no concentra mucho gasto, pero muestra señal prometedora. Útil para testear y asignar presupuesto incremental.',
  },
  ESTABLE: {
    label: 'Buen rendimiento',
    pillClass:
      'border-emerald-300/25 bg-emerald-400/10 text-emerald-100',
    description:
      'Rinde de forma consistente. Mantener y optimizar gradualmente para no romper performance.',
  },
  EN_DETERIORO: {
    label: 'En deterioro',
    pillClass:
      'border-amber-300/30 bg-amber-400/10 text-amber-100',
    description:
      'La performance viene cayendo respecto a su histórico. Revisar creatividades, segmentación y frecuencia.',
  },
  MAL_RENDIMIENTO: {
    label: 'Mal rendimiento',
    pillClass: 'border-rose-400/30 bg-rose-500/12 text-rose-100',
    description:
      'Está rindiendo peor que lo esperado para el objetivo. Candidato a ajustar, reducir presupuesto o pausar.',
  },
  ZOMBIE: {
    label: 'Zombie',
    pillClass:
      'border-white/15 bg-white/5 text-white/70',
    description:
      'Tiene muy poca señal útil (bajo gasto o sin resultados). Sirve como “sin decisión” hasta que acumule datos.',
  },
  SIN_DATOS: {
    label: 'Sin datos',
    pillClass:
      'border-white/15 bg-white/5 text-white/70',
    description:
      'No hay datos suficientes para clasificarlo (poco gasto o señales incompletas).',
  },
};

const getLabelUi = (label) => {
  const key = typeof label === 'string' ? label.trim().toUpperCase() : '';
  return LABEL_UI[key] || null;
};

// Aliases por si el backend usa otras keys (snake_case)
LABEL_UI.CONPOTENCIAL = LABEL_UI.CON_POTENCIAL;
LABEL_UI.ENDETERIORO = LABEL_UI.EN_DETERIORO;

const toAct = (id) => {
  if (!id) return '';
  const s = String(id).trim();
  if (!s) return '';
  if (s.startsWith('act_')) return s;
  if (/^\d+$/.test(s)) return `act_${s}`;
  return s;
};

const toFiniteNumberOrNull = (value) => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatMoney2 = (value) => {
  const n = toFiniteNumberOrNull(value);
  if (n === null) return '—';
  // Pedido: $1,20 (coma decimal). Usamos es-AR para moneda sin símbolo automático.
  return `$${new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}`;
};

const formatNumber2 = (value) => {
  const n = toFiniteNumberOrNull(value);
  if (n === null) return '—';
  return new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

// El backend devuelve el motivo del fallo como un string crudo que embebe el
// error de la Graph API de Facebook. Extraemos sólo los campos amigables
// (error_user_title / error_user_msg) para no mostrar el blob completo.
const extractMetaUserError = (rawError) => {
  if (!rawError) return null;

  const pick = (obj) => {
    if (!obj || typeof obj !== 'object') return null;
    const e = obj.error && typeof obj.error === 'object' ? obj.error : obj;
    const title = e?.error_user_title || null;
    const msg = e?.error_user_msg || null;
    return title || msg ? { title, msg } : null;
  };

  if (typeof rawError === 'object') {
    const found = pick(rawError);
    if (found) return found;
  }

  if (typeof rawError === 'string') {
    // 1) Intentar parsear el bloque JSON embebido ("Response: { ... }").
    const start = rawError.indexOf('{');
    const end = rawError.lastIndexOf('}');
    if (start !== -1 && end > start) {
      try {
        const found = pick(JSON.parse(rawError.slice(start, end + 1)));
        if (found) return found;
      } catch {
        /* sigue al fallback por regex */
      }
    }
    // 2) Fallback por regex (des-escapando \uXXXX y comillas).
    const unescape = (s) => {
      if (!s) return null;
      try {
        return JSON.parse(`"${s}"`);
      } catch {
        return s;
      }
    };
    const titleMatch = rawError.match(/"error_user_title"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    const msgMatch = rawError.match(/"error_user_msg"\s*:\s*"((?:[^"\\]|\\.)*)"/);
    const title = titleMatch ? unescape(titleMatch[1]) : null;
    const msg = msgMatch ? unescape(msgMatch[1]) : null;
    if (title || msg) return { title, msg };
  }

  return null;
};

const getCreativeTypeUi = (creativeType) => {
  const key = typeof creativeType === 'string' ? creativeType.trim().toUpperCase() : '';
  if (!key) return null;
  if (key === 'VIDEO') return { label: 'Video', className: 'border-sky-400/25 bg-sky-500/10 text-sky-100' };
  if (key === 'IMAGE') return { label: 'Imagen', className: 'border-violet-400/25 bg-violet-500/10 text-violet-100' };
  return { label: key, className: 'border-white/15 bg-white/5 text-white/70' };
};

const Pill = ({ children, className = '', title = '' }) => (
  <span
    title={title}
    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold tracking-wide ${className}`}
  >
    {children}
  </span>
);

const MetaCreativeRankingPlayground = () => {
  const { auth, hasMetaConnected } = useAuth();
  const { activeMetaAccount } = useAds();
  const { openConnectionsModal } = useContext(ModalsContext);
  const defaultAccount = useMemo(
    () => toAct(auth?.user?.meta_ad_account_id || '') || toAct(auth?.user?.brands?.find((b) => b.id === auth?.user?.active_brand_id)?.meta_ad_account_id) || '',
    [auth?.user]
  );

  const [showMetaConnectModal, setShowMetaConnectModal] = useState(false);

  useEffect(() => {
    if (!hasMetaConnected) setShowMetaConnectModal(true);
    else setShowMetaConnectModal(false);
  }, [hasMetaConnected]);

  const [accountId, setAccountId] = useState(defaultAccount);
  const [windowParam, setWindowParam] = useState('last_30_days');
  const [dateFrom, setDateFrom] = useState(() => format(subDays(new Date(), 14), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [objective, setObjective] = useState('roas');
  const [conversionTypesInput, setConversionTypesInput] = useState('purchase');
  const [selectedConversionTypes, setSelectedConversionTypes] = useState(['purchase']);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [expandedAdId, setExpandedAdId] = useState(null);
  const [expandedPreviewByCreativeId, setExpandedPreviewByCreativeId] = useState({});
  const [budgetContextByAdId, setBudgetContextByAdId] = useState({});
  const [applyingActionByAdId, setApplyingActionByAdId] = useState({}); // { [ad_id]: 'BUDGET_UP'|'BUDGET_DOWN'|'PAUSE' }
  const [budgetActionDraft, setBudgetActionDraft] = useState(null); // { ad_id, direction: 'up'|'down', mode: 'ABSOLUTE'|'DELTA_PCT'|'DELTA_ABS', value: number|string }
  const [pauseConfirmAdId, setPauseConfirmAdId] = useState(null);
  const previewHostRef = useRef(null);
  const previewContentRef = useRef(null);
  const [previewHostSize, setPreviewHostSize] = useState({ w: 0, h: 0 });
  const [previewContentSize, setPreviewContentSize] = useState({ w: 0, h: 0 });
  const [supportsZoom, setSupportsZoom] = useState(false);

  useEffect(() => {
    if (defaultAccount) setAccountId((prev) => (prev ? prev : defaultAccount));
  }, [defaultAccount]);

  // --- Job + polling machinery -------------------------------------------
  // El endpoint encola un job (202) y se hace polling de /api/job-status/<id>.
  const pollTimerRef = useRef(null); // setTimeout pendiente del próximo poll
  const runIdRef = useRef(0); // identifica el loop activo; al cambiar, los callbacks viejos abortan
  const inFlightKeyRef = useRef(null); // params del loop en curso (dedupe de kickoffs)
  const loadingRef = useRef(false); // espejo de `loading` para leerlo sin re-suscribir effects
  const startJobRef = useRef(null); // ref a startCreativeRankingJob (rompe el ciclo con schedulePoll)
  const runFetchRef = useRef(null); // ref a runFetch (para el effect de cambio de filtros)

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // Cancela cualquier loop en curso al desmontar (navegar fuera).
  useEffect(() => () => {
    runIdRef.current += 1;
    clearPoll();
  }, [clearPoll]);

  const buildRankingParams = useCallback(() => {
    const conversionTypes =
      selectedConversionTypes?.length > 0
        ? selectedConversionTypes
        : (conversionTypesInput.trim()
          ? conversionTypesInput.split(',').map((t) => t.trim()).filter(Boolean)
          : []);

    const params = {
      account_id: accountId.trim(),
      window: windowParam,
      objective,
      limit: 100,
    };
    if (windowParam === 'custom') {
      params.date_from = dateFrom;
      params.date_to = dateTo;
    }
    if (conversionTypes.length > 0) {
      params.conversion_types = conversionTypes;
    }
    return params;
  }, [accountId, windowParam, dateFrom, dateTo, objective, conversionTypesInput, selectedConversionTypes]);

  // Step 2 — poll de /api/job-status/<jobId> cada 3s hasta un estado terminal.
  const schedulePoll = useCallback((jobId, params, myRunId, attempt) => {
    clearPoll();
    pollTimerRef.current = setTimeout(async () => {
      if (runIdRef.current !== myRunId) return; // loop cancelado / reemplazado
      if (attempt >= RANKING_MAX_POLL_ATTEMPTS) {
        setError({
          error_code: 'TIMEOUT',
          message: 'El análisis está tardando más de lo esperado. Volvé a intentarlo.',
          retryable: true,
        });
        setLoading(false);
        inFlightKeyRef.current = null;
        return;
      }
      try {
        const data = await metaApi.getCreativeRankingJobStatus(jobId);
        if (runIdRef.current !== myRunId) return;
        const status = data?.status;
        if (status === 'completed') {
          setPayload(data?.result || null);
          setLoading(false);
          inFlightKeyRef.current = null;
        } else if (status === 'failed') {
          const userError = extractMetaUserError(data?.error);
          setError({
            error_code: 'JOB_FAILED',
            title: userError?.title || null,
            message:
              userError?.msg ||
              userError?.title ||
              'No pudimos completar el análisis. Volvé a intentarlo.',
            retryable: true,
          });
          setLoading(false);
          inFlightKeyRef.current = null;
        } else {
          // pending | running → seguir esperando.
          schedulePoll(jobId, params, myRunId, attempt + 1);
        }
      } catch (e) {
        if (runIdRef.current !== myRunId) return;
        if (e?.response?.status === 404) {
          // Job expirado (TTL ~10 min) → re-encolar uno nuevo, preservando el
          // contador de intentos para no quedar en un loop infinito.
          startJobRef.current?.(params, myRunId, attempt + 1);
        } else {
          setError({
            error_code: e?.response?.data?.error_code || 'JOB_STATUS_ERROR',
            message: e?.response?.data?.message || e?.message || 'Error consultando el estado del análisis.',
            retryable: true,
          });
          setLoading(false);
          inFlightKeyRef.current = null;
        }
      }
    }, RANKING_POLL_INTERVAL_MS);
  }, [clearPoll]);

  // Step 1 — kickoff: encola el job (202) y arranca el polling.
  const startCreativeRankingJob = useCallback(async (params, myRunId, startAttempt) => {
    try {
      const res = await metaApi.fetchMetaCreativeRanking(params);
      if (runIdRef.current !== myRunId) return;
      const data = res?.data;
      if (res?.status === 202 && data?.job_id) {
        // status puede ser "pending" o "already_running"; en ambos casos se poll-ea igual.
        schedulePoll(data.job_id, params, myRunId, startAttempt);
        return;
      }
      // Compat: si el backend devolviera el payload directo, renderizarlo.
      if (data && (Array.isArray(data.ads) || data.metadata || data.account_summary)) {
        setPayload(data);
        setLoading(false);
        inFlightKeyRef.current = null;
        return;
      }
      setError({
        error_code: 'UNEXPECTED',
        message: 'Respuesta inesperada del servidor. Volvé a intentarlo.',
        retryable: true,
      });
      setLoading(false);
      inFlightKeyRef.current = null;
    } catch (e) {
      if (runIdRef.current !== myRunId) return;
      const d = e?.response?.data;
      if (d?.error_code === 'META_NOT_CONNECTED') {
        setError({
          error_code: 'META_NOT_CONNECTED',
          message: d?.message || 'Conectá tu cuenta de Meta Ads para ver el ranking de creatividades.',
          metaNotConnected: true,
          retryable: false,
        });
        setLoading(false);
        inFlightKeyRef.current = null;
        return;
      }
      setError({
        error_code: d?.error_code || 'FRONT',
        message: d?.message || d?.error || e?.message || 'Error de red',
        retryable: true,
      });
      setLoading(false);
      inFlightKeyRef.current = null;
    }
  }, [schedulePoll]);

  useEffect(() => {
    startJobRef.current = startCreativeRankingJob;
  }, [startCreativeRankingJob]);

  const runFetch = useCallback(() => {
    const params = buildRankingParams();
    if (!params.account_id) return;
    const key = JSON.stringify(params);
    // De-dupe: no disparar otro kickoff si ya hay un loop en curso para los mismos params.
    if (loadingRef.current && inFlightKeyRef.current === key) return;
    // Cancelar cualquier loop previo antes de arrancar uno nuevo.
    clearPoll();
    const myRunId = runIdRef.current + 1;
    runIdRef.current = myRunId;
    inFlightKeyRef.current = key;
    setLoading(true);
    setError(null);
    setPayload(null);
    startCreativeRankingJob(params, myRunId, 0);
  }, [buildRankingParams, clearPoll, startCreativeRankingJob]);

  useEffect(() => {
    runFetchRef.current = runFetch;
  }, [runFetch]);

  // Si cambian los filtros mientras hay un análisis en curso, cancelar el loop
  // viejo y arrancar uno nuevo con los params nuevos (nunca dos loops a la vez).
  const rankingParamsKey = useMemo(
    () => JSON.stringify(buildRankingParams()),
    [buildRankingParams]
  );
  const isFirstParamsEffectRef = useRef(true);
  useEffect(() => {
    if (isFirstParamsEffectRef.current) {
      isFirstParamsEffectRef.current = false;
      return;
    }
    if (loadingRef.current) {
      runFetchRef.current?.();
    }
  }, [rankingParamsKey]);

  const openRow = useCallback(async (ad) => {
    if (!ad?.ad_id) return;
    if (expandedAdId === ad.ad_id) {
      setExpandedAdId(null);
      return;
    }
    setExpandedAdId(ad.ad_id);
    const creativeId = ad.creative_id;
    if (!creativeId) return;
    if (expandedPreviewByCreativeId[creativeId]?.html !== undefined) return;
    setExpandedPreviewByCreativeId((prev) => ({ ...prev, [creativeId]: { isLoading: true, html: undefined, meta: null, error: false } }));
    try {
      const res = await metaApi.fetchMetaCreativeById(creativeId, 'MOBILE_FEED_STANDARD', { responseFormat: 'object' });
      const d = res.data;
      const html = d && typeof d === 'object' && 'preview_html' in d ? d.preview_html : (typeof d === 'string' ? d : null);
      setExpandedPreviewByCreativeId((prev) => ({ ...prev, [creativeId]: { isLoading: false, html, meta: d, error: false } }));
    } catch {
      setExpandedPreviewByCreativeId((prev) => ({ ...prev, [creativeId]: { isLoading: false, html: null, meta: null, error: true } }));
    }
  }, [expandedAdId, expandedPreviewByCreativeId]);

  const meta = payload?.metadata;
  const summary = payload?.account_summary;
  const ads = useMemo(
    () => (Array.isArray(payload?.ads) ? payload.ads : []),
    [payload?.ads]
  );
  const selectedAd = useMemo(
    () => (expandedAdId ? ads.find((a) => a?.ad_id === expandedAdId) : null),
    [ads, expandedAdId]
  );

  const hasApplyingAction = useCallback((adId) => {
    if (!adId) return false;
    return !!applyingActionByAdId?.[adId];
  }, [applyingActionByAdId]);

  const isApplyingAction = useCallback((adId, key) => {
    if (!adId) return false;
    const v = applyingActionByAdId?.[adId];
    if (!v) return false;
    if (!key) return true;
    return v === key;
  }, [applyingActionByAdId]);

  const refreshBudgetContext = useCallback(async (adId) => {
    if (!adId) return null;
    setBudgetContextByAdId((prev) => ({
      ...(prev || {}),
      [adId]: { ...(prev?.[adId] || {}), isLoading: true, error: null },
    }));
    try {
      const res = await metaApi.fetchMetaAdBudgetContext(adId, { account_id: accountId.trim() });
      const rid = res.headers?.['x-request-id'] || res.headers?.['X-Request-Id'] || res.data?.request_id || null;
      const data = res.data;
      const attempted = data?.execution?.attempted;
      const executed = data?.execution?.executed;
      const err = data?.execution?.error;
      if (attempted && executed === false && err) toast.error(err);
      setBudgetContextByAdId((prev) => ({
        ...(prev || {}),
        [adId]: { isLoading: false, error: null, requestId: rid, data },
      }));
      return data ?? null;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'No se pudo resolver presupuesto.';
      setBudgetContextByAdId((prev) => ({
        ...(prev || {}),
        [adId]: { isLoading: false, error: msg, requestId: null, data: null },
      }));
      toast.error(msg);
      return null;
    }
  }, [accountId]);

  const executeQuickAction = useCallback(async (adId, body, { successMessage, actionKey } = {}) => {
    if (!adId) return null;
    if (hasApplyingAction(adId)) return null;
    setApplyingActionByAdId((prev) => ({ ...(prev || {}), [adId]: actionKey || true }));
    try {
      const res = await metaApi.executeMetaAdAction(body);
      const data = res.data;
      const attempted = data?.execution?.attempted;
      const executed = data?.execution?.executed;
      const err = data?.execution?.error;
      if (attempted && executed === false) {
        toast.error(err || 'No se pudo ejecutar el cambio en Meta.');
      } else if (executed) {
        toast.success(successMessage || 'Cambio ejecutado en Meta.');
      } else {
        toast.success(successMessage || 'Acción aplicada.');
      }
      refreshBudgetContext(adId);
      return data ?? null;
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'No se pudo ejecutar la acción.';
      toast.error(msg);
      return null;
    } finally {
      setApplyingActionByAdId((prev) => {
        const next = { ...(prev || {}) };
        delete next[adId];
        return next;
      });
    }
  }, [hasApplyingAction, refreshBudgetContext]);

  useEffect(() => {
    if (!selectedAd?.ad_id) return;
    const adId = selectedAd.ad_id;
    const cached = budgetContextByAdId?.[adId];
    if (cached?.data || cached?.isLoading) return;
    refreshBudgetContext(adId);
  }, [selectedAd?.ad_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const objectiveLabel = objective === 'cpa' ? 'CPA' : 'ROAS';
  const availableConversionTypesFromRanking = useMemo(
    () => meta?.available_conversion_types?.canonical_inputs || [],
    [meta?.available_conversion_types?.canonical_inputs]
  );
  const availableConversionTypesFromAccount = useMemo(() => {
    const pixels = activeMetaAccount?.pixels?.data || [];
    const events = pixels.flatMap((p) => (p?.existing_events || []).map((e) => String(e || '').trim()).filter(Boolean));
    const canonical = events
      .map((e) => e.toLowerCase())
      .map((e) => e.replace(/\s+/g, '_'))
      .map((e) => e.replace(/[^a-z0-9_]/g, ''))
      .filter(Boolean);
    return Array.from(new Set(canonical)).sort();
  }, [activeMetaAccount?.pixels?.data]);

  // Preferimos traer conversiones “antes” vía cuenta activa; fallback al ranking si existe.
  const availableConversionTypes = useMemo(
    () => (availableConversionTypesFromAccount?.length
      ? availableConversionTypesFromAccount
      : availableConversionTypesFromRanking) || [],
    [availableConversionTypesFromAccount, availableConversionTypesFromRanking]
  );

  const totalSpendAll = useMemo(() => {
    const fromSummary = toFiniteNumberOrNull(summary?.total_spend);
    if (fromSummary != null) return fromSummary;
    const sum = (ads || []).reduce((acc, a) => acc + (toFiniteNumberOrNull(a?.spend) || 0), 0);
    return sum;
  }, [summary?.total_spend, ads]);

  const labelSummaryCards = useMemo(() => {
    const list = Array.isArray(ads) ? ads : [];
    const by = new Map();
    for (const ad of list) {
      const labelKey = (typeof ad?.label === 'string' && ad.label.trim()) ? ad.label.trim().toUpperCase() : 'SIN_DATOS';
      const group = by.get(labelKey) || { labelKey, ads: [] };
      group.ads.push(ad);
      by.set(labelKey, group);
    }

    const cards = [];
    for (const [labelKey, group] of by.entries()) {
      const ui = getLabelUi(labelKey) || getLabelUi(group.ads?.[0]?.label) || { label: labelKey, pillClass: 'border-white/15 bg-white/5 text-white/80', description: '' };
      const adsWithSpend = group.ads.filter((a) => (toFiniteNumberOrNull(a?.spend) || 0) > 0);
      const activeCount = group.ads.filter((a) => String(a?.status || '').toUpperCase() === 'ACTIVE').length;
      const spendSum = adsWithSpend.reduce((acc, a) => acc + (toFiniteNumberOrNull(a?.spend) || 0), 0);

      let kpiAgg = null;
      if (objective === 'roas') {
        // best-effort: value ≈ roas * spend
        const valueSum = adsWithSpend.reduce((acc, a) => {
          const r = toFiniteNumberOrNull(a?.roas);
          const s = toFiniteNumberOrNull(a?.spend) || 0;
          return acc + (r != null ? r * s : 0);
        }, 0);
        kpiAgg = spendSum > 0 ? valueSum / spendSum : null;
      } else {
        const convSum = group.ads.reduce((acc, a) => acc + (Number(a?.conversions) || 0), 0);
        kpiAgg = convSum > 0 ? spendSum / convSum : null;
      }

      const spendPct = totalSpendAll > 0 ? (spendSum / totalSpendAll) * 100 : null;

      cards.push({
        labelKey,
        ui,
        activeCount,
        kpiAgg,
        spendPct,
      });
    }

    // Orden: si hay label priority implícito en LABEL_UI lo dejamos por orden de aparición; si no, por % gasto desc.
    return cards.sort((a, b) => (toFiniteNumberOrNull(b.spendPct) || 0) - (toFiniteNumberOrNull(a.spendPct) || 0));
  }, [ads, objective, totalSpendAll]);

  const [creativeTypeDetailOpen, setCreativeTypeDetailOpen] = useState(false);
  const [creativeTypeDetailTab, setCreativeTypeDetailTab] = useState('ads'); // ads|spend|conversions|kpi|top

  const creativeTypeSummaryCards = useMemo(() => {
    const list = Array.isArray(ads) ? ads : [];
    const by = new Map();
    for (const ad of list) {
      const key = typeof ad?.creative_type === 'string' && ad.creative_type.trim()
        ? ad.creative_type.trim().toUpperCase()
        : 'OTHER';
      const group = by.get(key) || { key, ads: [] };
      group.ads.push(ad);
      by.set(key, group);
    }

    const cards = [];
    for (const [key, group] of by.entries()) {
      const ui = getCreativeTypeUi(key) || { label: key, className: 'border-white/15 bg-white/5 text-white/80' };
      const adsWithSpend = group.ads.filter((a) => (toFiniteNumberOrNull(a?.spend) || 0) > 0);
      const spendSum = adsWithSpend.reduce((acc, a) => acc + (toFiniteNumberOrNull(a?.spend) || 0), 0);
      const activeCount = group.ads.filter((a) => String(a?.status || '').toUpperCase() === 'ACTIVE').length;
      const conversionsSum = group.ads.reduce((acc, a) => acc + (Number(a?.conversions) || 0), 0);

      let kpiAgg = null;
      if (objective === 'roas') {
        const valueSum = adsWithSpend.reduce((acc, a) => {
          const r = toFiniteNumberOrNull(a?.roas);
          const s = toFiniteNumberOrNull(a?.spend) || 0;
          return acc + (r != null ? r * s : 0);
        }, 0);
        kpiAgg = spendSum > 0 ? valueSum / spendSum : null;
      } else {
        const convSum = group.ads.reduce((acc, a) => acc + (Number(a?.conversions) || 0), 0);
        kpiAgg = convSum > 0 ? spendSum / convSum : null;
      }

      const spendPct = totalSpendAll > 0 ? (spendSum / totalSpendAll) * 100 : null;
      cards.push({ key, ui, count: group.ads.length, activeCount, kpiAgg, spendPct, spendSum, conversionsSum });
    }

    return cards.sort((a, b) => (toFiniteNumberOrNull(b.spendPct) || 0) - (toFiniteNumberOrNull(a.spendPct) || 0));
  }, [ads, objective, totalSpendAll]);

  useEffect(() => {
    const el = previewHostRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const cr = entry.contentRect;
      setPreviewHostSize({ w: cr.width || 0, h: cr.height || 0 });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    try {
      setSupportsZoom(typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('zoom', '1'));
    } catch {
      setSupportsZoom(false);
    }
  }, []);

  // Medir tamaño real del HTML (sin transform) para escalar “a medida”.
  useEffect(() => {
    const el = previewContentRef.current;
    if (!el) return;

    // 1) Medición inmediata (por si todavía no hay RO)
    const measure = () => {
      const w = el.scrollWidth || el.offsetWidth || 0;
      const h = el.scrollHeight || el.offsetHeight || 0;
      if (w > 0 && h > 0) setPreviewContentSize({ w, h });
    };
    measure();

    // 2) Medición reactiva: el preview de Meta suele cambiar después (assets, video, fuentes)
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }

    // 3) Reintentos cortos por si hay cambios sin resize observable
    const id1 = window.setTimeout(measure, 50);
    const id2 = window.setTimeout(measure, 250);
    const id3 = window.setTimeout(measure, 800);
    return () => {
      window.clearTimeout(id1);
      window.clearTimeout(id2);
      window.clearTimeout(id3);
      if (ro) ro.disconnect();
    };
  }, [expandedAdId, expandedPreviewByCreativeId]);

  // Si el backend trae canonical_inputs, inicializar selección (si todavía no hay selección explícita).
  useEffect(() => {
    if (!Array.isArray(availableConversionTypes) || availableConversionTypes.length === 0) return;
    setSelectedConversionTypes((prev) => (prev && prev.length > 0 ? prev : [availableConversionTypes[0]]));
  }, [availableConversionTypes]);

  const toggleConversionType = (ct) => {
    setSelectedConversionTypes((prev) => {
      const cur = Array.isArray(prev) ? prev : [];
      if (cur.includes(ct)) return cur.filter((x) => x !== ct);
      return [...cur, ct];
    });
  };

  return (
    <div className="min-h-full w-full bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-4 sm:p-6 text-white">
      <GenericModal
        isOpen={showMetaConnectModal}
        toggle={() => {}}
        title="Conectá tu cuenta de Meta Ads"
        description="Para utilizar esta sección necesitás conectar tu cuenta de Meta Ads."
        showLogo={false}
        {...NON_DISMISSIBLE_MODAL_PROPS}
        showSubmitButton
        submitButtonText="Ir a conexiones"
        onSubmit={() => {
          // Cerrar este modal antes de abrir Conexiones, para que no quede bloqueando clicks.
          setShowMetaConnectModal(false);
          // Abrir en el próximo tick para asegurar que el overlay se desmontó.
          window.setTimeout(() => {
          openConnectionsModal?.();
          }, 0);
        }}
      >
        <div className="text-sm text-white/80 leading-relaxed">
          Conectá Meta Ads desde <span className="text-white font-semibold">Conexiones</span> para ver el ranking y ejecutar acciones.
        </div>
      </GenericModal>

      <div className="max-w-7xl mx-auto space-y-4">
        <div className={card}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="text-white/70 text-sm font-semibold">KPI</div>
              <div
                className={`relative w-full max-w-sm rounded-full p-1 border transition-all ${
                  objective === 'roas'
                    ? 'border-emerald-400/50 shadow-[0_0_20px_rgba(16,185,129,0.25)]'
                    : 'border-fuchsia-400/50 shadow-[0_0_20px_rgba(217,70,239,0.25)]'
                } bg-white/5`}
              >
                <div
                  className={`absolute inset-y-1 w-1/2 rounded-full transition-transform duration-300 ease-out ${
                    objective === 'roas'
                      ? 'bg-emerald-500/20 ring-1 ring-emerald-400/40'
                      : 'bg-fuchsia-500/20 ring-1 ring-fuchsia-400/40 translate-x-full'
                  }`}
                />
                <div className="relative grid grid-cols-2">
                  <button
                    type="button"
                    onClick={() => setObjective('roas')}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      objective === 'roas' ? 'text-emerald-100' : 'text-white/70 hover:text-white'
                    } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40`}
                  >
                    ROAS
                  </button>
                  <button
                    type="button"
                    onClick={() => setObjective('cpa')}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                      objective === 'cpa' ? 'text-fuchsia-100' : 'text-white/70 hover:text-white'
                    } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fuchsia-500/40`}
                  >
                    CPA
                  </button>
                </div>
              </div>
              {objective === 'cpa' ? (
                <div className="space-y-2 pt-2">
                  <div className="text-white/70 text-sm font-semibold">Conversiones</div>
                  {Array.isArray(availableConversionTypes) && availableConversionTypes.length > 0 ? (
                    <div className="rounded-2xl bg-gradient-to-br from-slate-900/40 via-slate-800/30 to-indigo-900/30 border border-white/15 p-3 max-h-40 overflow-auto">
                      <div className="flex flex-wrap gap-2">
                        {availableConversionTypes.map((ct) => {
                          const checked = selectedConversionTypes.includes(ct);
                          return (
                            <button
                              key={ct}
                              type="button"
                              onClick={() => toggleConversionType(ct)}
                              className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-mono transition ${
                                checked
                                  ? 'border-fuchsia-400/50 bg-fuchsia-500/15 text-white shadow-[0_0_18px_rgba(217,70,239,0.18)]'
                                  : 'border-white/15 bg-white/5 text-white/75 hover:bg-white/10'
                              }`}
                            >
                              {ct}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <input
                      className="rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-white"
                      value={conversionTypesInput}
                      onChange={(e) => setConversionTypesInput(e.target.value)}
                      placeholder="purchase,add_to_cart"
                    />
                  )}
                </div>
              ) : null}
            </div>

            <div className="space-y-3">
              <div className="space-y-2">
                <div className="text-white/70 text-sm font-semibold">Ventana</div>
                {/* Selector píldora neon (similar a KPI) */}
                <div className="relative w-full rounded-full p-1 border border-sky-400/35 shadow-[0_0_18px_rgba(56,189,248,0.15)] bg-white/5">
                  <div
                    className={`absolute inset-y-1 w-1/3 rounded-full transition-transform duration-300 ease-out bg-sky-500/15 ring-1 ring-sky-400/30 ${
                      windowParam === 'yesterday' ? '' : windowParam === 'last_30_days' ? 'translate-x-full' : 'translate-x-[200%]'
                    }`}
                  />
                  <div className="relative grid grid-cols-3">
                    <button
                      type="button"
                      onClick={() => setWindowParam('yesterday')}
                      className={`rounded-full px-3 py-2 text-xs sm:text-sm font-semibold transition ${
                        windowParam === 'yesterday' ? 'text-sky-100' : 'text-white/70 hover:text-white'
                      } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40`}
                    >
                      Ayer
                    </button>
                    <button
                      type="button"
                      onClick={() => setWindowParam('last_30_days')}
                      className={`rounded-full px-3 py-2 text-xs sm:text-sm font-semibold transition ${
                        windowParam === 'last_30_days' ? 'text-sky-100' : 'text-white/70 hover:text-white'
                      } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40`}
                    >
                      30 días
                    </button>
                    <button
                      type="button"
                      onClick={() => setWindowParam('custom')}
                      className={`rounded-full px-3 py-2 text-xs sm:text-sm font-semibold transition ${
                        windowParam === 'custom' ? 'text-sky-100' : 'text-white/70 hover:text-white'
                      } focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/40`}
                    >
                      Personalizado
                    </button>
                  </div>
                </div>
              </div>
              {windowParam === 'custom' ? (
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-white/70">Desde</span>
                    <input
                      type="date"
                      className="rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-white"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    <span className="text-white/70">Hasta</span>
                    <input
                      type="date"
                      className="rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-white"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </label>
                </div>
              ) : null}
            </div>
          </div>
          <div className="mt-5 flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={runFetch}
              disabled={loading || !accountId.trim()}
              className="relative inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              <span className="relative inline-flex items-center gap-2 rounded-full bg-slate-950/80 border border-white/15 px-6 py-3 shadow-[0_0_25px_rgba(217,70,239,0.25)]">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                Analizar mis Ads
              </span>
            </button>
          </div>
        </div>

        {payload && labelSummaryCards.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {labelSummaryCards.map((c) => (
              <div
                key={c.labelKey}
                className="backdrop-blur-xl bg-gradient-to-br from-slate-800/70 via-slate-900/70 to-indigo-900/70 border border-white/15 rounded-2xl shadow-lg p-4 text-white/90"
              >
                <div className="flex items-start justify-between gap-2">
                  <Pill className={c.ui?.pillClass || 'border-white/15 bg-white/5 text-white/80'}>
                    {c.ui?.label || c.labelKey}
                  </Pill>
                  <span className="text-[11px] text-white/50">
                    {c.activeCount} activos
                  </span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] text-white/50">{objectiveLabel}</div>
                    <div className="font-mono text-sm text-white">
                      {objective === 'cpa' ? formatMoney2(c.kpiAgg) : formatNumber2(c.kpiAgg)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] text-white/50">% inversión</div>
                    <div className="font-mono text-sm text-white">
                      {c.spendPct == null ? '—' : `${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 }).format(c.spendPct)}%`}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {payload && creativeTypeSummaryCards.length > 0 ? (
          <button
            type="button"
            onClick={() => setCreativeTypeDetailOpen(true)}
            className="w-full text-left backdrop-blur-xl bg-gradient-to-br from-slate-800/70 via-slate-900/70 to-indigo-900/70 border border-white/15 rounded-2xl shadow-lg p-4 text-white/90 hover:bg-white/10 transition"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white font-semibold">Ver detalle por tipo de creatividad</div>
                <div className="text-xs text-white/60 mt-1">
                  Cantidad de anuncios, inversión y conversiones por tipo (video / imagen / carrusel).
                </div>
              </div>
              <span className="text-xs text-fuchsia-200 border border-fuchsia-400/25 bg-fuchsia-500/10 rounded-full px-3 py-1">
                Abrir
              </span>
            </div>
          </button>
        ) : null}

        {loading && !payload && !error && (
          <div className={card}>
            <div className="flex items-center gap-3 text-white/70">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Analizando tus anuncios… esto puede tardar unos segundos.</span>
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className="h-24 rounded-xl border border-white/10 bg-white/5 animate-pulse"
                />
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className={`${card} border-rose-500/40`}>
            <div className="flex items-start gap-2 text-rose-200">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <div className="flex-1">
                <p className="font-semibold">
                  {error.metaNotConnected
                    ? 'Conectá Meta Ads'
                    : (error.title || error.error_code || 'Error')}
                </p>
                <p className="text-sm text-white/80 mt-1">{error.message}</p>
                {(error.metaNotConnected || error.retryable) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {error.metaNotConnected ? (
                      <button
                        type="button"
                        onClick={() => openConnectionsModal?.()}
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15 transition"
                      >
                        Conectar Meta Ads
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={runFetch}
                        className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15 transition"
                      >
                        <Play className="h-4 w-4" />
                        Reintentar
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {payload && (
          <div className={card}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="text-sm font-medium text-white/80">Anuncios</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-white/50 font-mono">{ads.length} items</span>
              </div>
            </div>

            {ads.length === 0 ? (
              <div className="py-10 text-white/50 text-center">Sin filas en esta página.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {ads.map((ad) => {
                  const labelUi = getLabelUi(ad.label);
                  const spendPct =
                    ad.spend_share != null
                      ? Number(ad.spend_share)
                      : (totalSpendAll > 0 ? ((toFiniteNumberOrNull(ad.spend) || 0) / totalSpendAll) * 100 : null);
                  const kpiValue =
                    objective === 'roas'
                      ? formatNumber2(ad.roas)
                      : formatMoney2(ad.cpa);
                  return (
                    <div
                      key={ad.ad_id}
                      className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors p-2 flex flex-col gap-2"
                    >
                      <div className="text-[11px] leading-tight text-white/90 line-clamp-2 min-h-[32px]">
                        {ad.ad_name || ad.ad_id}
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        {ad.label ? (
                          <Pill className={`${labelUi?.pillClass || 'border-white/15 bg-white/5 text-white/80'} px-1.5 py-0 text-[10px]`}>
                            {labelUi?.label || ad.label}
                          </Pill>
                        ) : (
                          <span className="text-[11px] text-white/40">—</span>
                        )}
                        <span
                          className="text-[11px] font-mono text-white/60"
                          title="% de inversión del total (share de gasto)"
                        >
                          {spendPct == null || Number.isNaN(spendPct)
                            ? 'Inv. —'
                            : `Inv. ${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 }).format(spendPct)}%`}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] text-white/50">{objectiveLabel}</span>
                        <span className="text-[11px] font-mono text-white/85">{kpiValue}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => openRow(ad)}
                        className="mt-auto rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-xs text-white/80 px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                      >
                        Ver más
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {creativeTypeDetailOpen ? (
          <div className="fixed inset-0 z-[9500]" role="dialog" aria-modal="true" aria-label="Detalle por tipo de creatividad">
            <button
              type="button"
              aria-label="Cerrar detalle"
              onClick={() => setCreativeTypeDetailOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
            />
            <div className="absolute inset-0 flex items-center justify-center p-2 sm:p-4">
              <div className="w-full max-w-5xl h-[92vh] sm:h-auto sm:max-h-[90vh] overflow-hidden backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl shadow-2xl p-3 sm:p-5 text-white/90 flex flex-col">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white font-semibold">Detalle por tipo de creatividad</div>
                    <div className="text-xs text-white/60 mt-1">
                      {objectiveLabel} · Inv. sobre el total · Conversiones (si aplica)
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCreativeTypeDetailOpen(false)}
                    className="text-xs text-white/70 hover:text-white border border-white/15 bg-white/5 rounded-full px-3 py-1"
                  >
                    Cerrar
                  </button>
                </div>

                {(() => {
                  // Para garantizar que el modal no scrollee, limitamos a los 4 tipos más relevantes.
                  const types = creativeTypeSummaryCards.slice(0, 4);
                  const chartDataCount = types.map((c) => ({ name: c.ui?.label || c.key, value: c.count }));
                  const chartDataSpend = types.map((c) => ({ name: c.ui?.label || c.key, value: toFiniteNumberOrNull(c.spendSum) || 0 }));
                  const chartDataConv = types.map((c) => ({ name: c.ui?.label || c.key, value: c.conversionsSum || 0 }));
                  const chartDataKpi = types.map((c) => ({ name: c.ui?.label || c.key, value: toFiniteNumberOrNull(c.kpiAgg) }));
                  return (
                    <>
                {/* Mobile: tabs (sin scroll) */}
                <div className="mt-3 lg:hidden">
                  <div className="flex flex-wrap gap-2">
                    {[
                      ['ads', 'Ads'],
                      ['spend', 'Inv.'],
                      ['conversions', 'Conv.'],
                      ['kpi', objectiveLabel],
                      ['top', 'Top'],
                    ].map(([id, label]) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setCreativeTypeDetailTab(id)}
                        className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                          creativeTypeDetailTab === id
                            ? 'border-fuchsia-400/40 bg-fuchsia-500/10 text-white'
                            : 'border-white/15 bg-white/5 text-white/70 hover:bg-white/10'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="mt-3 flex-1 min-h-0">
                    {creativeTypeDetailTab !== 'top' ? (
                      <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
                        <div className="h-[56vh]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={
                                creativeTypeDetailTab === 'ads'
                                  ? chartDataCount
                                  : creativeTypeDetailTab === 'spend'
                                    ? chartDataSpend
                                    : creativeTypeDetailTab === 'conversions'
                                      ? chartDataConv
                                      : chartDataKpi
                              }
                            >
                              <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                              <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                              <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                              <Tooltip
                                contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, color: 'white', fontSize: 12 }}
                                formatter={(v) => {
                                  if (creativeTypeDetailTab === 'spend') return [formatMoney2(v), 'Inversión'];
                                  if (creativeTypeDetailTab === 'kpi') {
                                    if (v == null) return ['—', objectiveLabel];
                                    const n = Number(v);
                                    if (!Number.isFinite(n)) return ['—', objectiveLabel];
                                    return [objective === 'cpa' ? formatMoney2(n) : formatNumber2(n), objectiveLabel];
                                  }
                                  return [v, ''];
                                }}
                              />
                              <Bar
                                dataKey="value"
                                fill={
                                  creativeTypeDetailTab === 'ads'
                                    ? 'rgba(217, 70, 239, 0.85)'
                                    : creativeTypeDetailTab === 'spend'
                                      ? 'rgba(56, 189, 248, 0.75)'
                                      : creativeTypeDetailTab === 'conversions'
                                        ? 'rgba(16, 185, 129, 0.65)'
                                        : 'rgba(245, 158, 11, 0.65)'
                                }
                                radius={[6, 6, 0, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
                        <div className="text-xs font-semibold text-white/70 mb-2">Destacados por tipo</div>
                        <div className="grid grid-cols-1 gap-2">
                          {types.map((c) => {
                            const key = c.key;
                            const candidates = (Array.isArray(ads) ? ads : []).filter((a) => String(a?.creative_type || '').toUpperCase() === key);
                            const kpiOrNull = (ad) => {
                              const raw = objective === 'roas' ? ad?.roas : ad?.cpa;
                              const n = toFiniteNumberOrNull(raw);
                              if (n == null) return null;
                              if (n === 0) return null; // Regla: 0 se trata como NULL
                              return n;
                            };
                            const top = candidates
                              .filter((a) => kpiOrNull(a) != null)
                              .slice()
                              .sort((a, b) => {
                                const ka = kpiOrNull(a);
                                const kb = kpiOrNull(b);
                                if (ka == null && kb == null) return 0;
                                if (ka == null) return 1;
                                if (kb == null) return -1;
                                return objective === 'roas' ? (kb - ka) : (ka - kb);
                              })[0];
                            if (!top) return null;
                            const labelUi = getLabelUi(top.label);
                            const spendPct = top.spend_share != null ? Number(top.spend_share) : null;
                            const kpiValue = objective === 'roas' ? formatNumber2(top.roas) : formatMoney2(top.cpa);
                            return (
                              <div key={key} className="rounded-xl border border-white/10 bg-white/5 p-2">
                                <div className="flex items-center justify-between gap-2">
                                  <Pill className={c.ui?.className || 'border-white/15 bg-white/5 text-white/80'}>
                                    {c.ui?.label || key}
                                  </Pill>
                                  <span className="text-[11px] font-mono text-white/60">{spendPct == null ? '—' : `Inv. ${spendPct.toFixed(1)}%`}</span>
                                </div>
                                <div className="mt-2 text-[12px] text-white/90 line-clamp-2">{top.ad_name || top.ad_id}</div>
                                <div className="mt-2 flex items-center justify-between gap-2">
                                  {top.label ? (
                                    <Pill className={labelUi?.pillClass || 'border-white/15 bg-white/5 text-white/80'}>
                                      {labelUi?.label || top.label}
                                    </Pill>
                                  ) : null}
                                  <span className="text-[11px] font-mono text-white/80">{objectiveLabel} {kpiValue}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => { setCreativeTypeDetailOpen(false); openRow(top); }}
                                  className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-[11px] text-white/80 px-2 py-1.5"
                                >
                                  Ver más
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Desktop: 2x2 + destacados */}
                <div className="mt-3 hidden lg:block">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {/* Ads count */}
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
                    <div className="text-xs font-semibold text-white/70 mb-2">Cantidad de anuncios</div>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataCount}>
                          <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                          <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, color: 'white', fontSize: 12 }}
                          />
                          <Bar dataKey="value" fill="rgba(217, 70, 239, 0.85)" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Spend */}
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
                    <div className="text-xs font-semibold text-white/70 mb-2">Inversión</div>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataSpend}>
                          <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                          <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, color: 'white', fontSize: 12 }}
                            formatter={(v) => [formatMoney2(v), 'Inversión']}
                          />
                          <Bar dataKey="value" fill="rgba(56, 189, 248, 0.75)" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Conversions */}
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
                    <div className="text-xs font-semibold text-white/70 mb-2">Conversiones</div>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartDataConv}>
                          <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                          <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, color: 'white', fontSize: 12 }}
                          />
                          <Bar dataKey="value" fill="rgba(16, 185, 129, 0.65)" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* KPI */}
                  <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
                    <div className="text-xs font-semibold text-white/70 mb-2">{objectiveLabel} por tipo</div>
                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={chartDataKpi}
                        >
                          <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                          <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                          <Tooltip
                            contentStyle={{ background: 'rgba(15,23,42,0.95)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, color: 'white', fontSize: 12 }}
                            formatter={(v) => {
                              if (v == null) return ['—', objectiveLabel];
                              const n = Number(v);
                              if (!Number.isFinite(n)) return ['—', objectiveLabel];
                              return [objective === 'cpa' ? formatMoney2(n) : formatNumber2(n), objectiveLabel];
                            }}
                          />
                          <Bar dataKey="value" fill="rgba(245, 158, 11, 0.65)" radius={[6, 6, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Destacados */}
                <div className="mt-3 rounded-2xl border border-white/15 bg-white/5 p-3">
                  <div className="text-xs font-semibold text-white/70 mb-2">Destacados por tipo</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
                    {types.map((c) => {
                      const key = c.key;
                      const candidates = (Array.isArray(ads) ? ads : []).filter((a) => String(a?.creative_type || '').toUpperCase() === key);
                      const kpiOrNull = (ad) => {
                        const raw = objective === 'roas' ? ad?.roas : ad?.cpa;
                        const n = toFiniteNumberOrNull(raw);
                        if (n == null) return null;
                        if (n === 0) return null; // Regla: 0 se trata como NULL
                        return n;
                      };
                      const top = candidates
                        .filter((a) => kpiOrNull(a) != null)
                        .slice()
                        .sort((a, b) => {
                          const ka = kpiOrNull(a);
                          const kb = kpiOrNull(b);
                          if (ka == null && kb == null) return 0;
                          if (ka == null) return 1;
                          if (kb == null) return -1;
                          return objective === 'roas' ? (kb - ka) : (ka - kb);
                        })[0];
                      if (!top) return null;
                      const labelUi = getLabelUi(top.label);
                      const spendPct = top.spend_share != null ? Number(top.spend_share) : null;
                      const kpiValue = objective === 'roas' ? formatNumber2(top.roas) : formatMoney2(top.cpa);
                      return (
                        <div key={key} className="rounded-xl border border-white/10 bg-white/5 p-2">
                          <div className="flex items-center justify-between gap-2">
                            <Pill className={c.ui?.className || 'border-white/15 bg-white/5 text-white/80'}>
                              {c.ui?.label || key}
                            </Pill>
                            <span className="text-[11px] font-mono text-white/60">{spendPct == null ? '—' : `Inv. ${spendPct.toFixed(1)}%`}</span>
                          </div>
                          <div className="mt-2 text-[12px] text-white/90 line-clamp-2 min-h-[32px]">{top.ad_name || top.ad_id}</div>
                          <div className="mt-2 flex items-center justify-between gap-2">
                            {top.label ? (
                              <Pill className={labelUi?.pillClass || 'border-white/15 bg-white/5 text-white/80'}>
                                {labelUi?.label || top.label}
                              </Pill>
                            ) : null}
                            <span className="text-[11px] font-mono text-white/80">{objectiveLabel} {kpiValue}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => { setCreativeTypeDetailOpen(false); openRow(top); }}
                            className="mt-2 w-full rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-[11px] text-white/80 px-2 py-1.5"
                          >
                            Ver más
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : null}

        {selectedAd && (() => {
          const creativeId = selectedAd.creative_id;
          const creativePreview = creativeId ? expandedPreviewByCreativeId[creativeId] : null;
          const trendPoints = Array.isArray(selectedAd.trend_points) ? selectedAd.trend_points : null;
          const trendData = trendPoints
            ? trendPoints.map((p, idx) => ({
              name: p.week || `W${idx + 1}`,
              value: toFiniteNumberOrNull(p.kpi),
            }))
            : (Array.isArray(selectedAd.trend)
              ? selectedAd.trend.map((v, idx) => ({ name: `W${idx + 1}`, value: toFiniteNumberOrNull(v) }))
              : []);

          return (
            <div
              className="fixed inset-0 z-[9999]"
              role="dialog"
              aria-modal="true"
              aria-label="Detalle del anuncio"
            >
              {/* overlay */}
              <button
                type="button"
                aria-label="Cerrar detalle"
                onClick={() => setExpandedAdId(null)}
                className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
              />

              {/* drawer */}
              <div
                className="absolute inset-y-0 right-0 w-full sm:w-[560px] lg:w-[720px] border-l border-white/15 bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 shadow-2xl backdrop-blur-xl transform transition-transform duration-300 ease-out translate-x-0"
              >
                <div className="h-full flex flex-col">
                  <div className="p-4 sm:p-6 border-b border-white/10">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-xs text-white/60">Detalle del anuncio</div>
                        <h3 className="text-white text-lg font-semibold truncate">{selectedAd.ad_name || selectedAd.ad_id}</h3>
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpandedAdId(null)}
                        className="text-xs text-white/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded px-2 py-1"
                      >
                        Cerrar
                      </button>
                    </div>
                  </div>

                  {/* Layout sin scroll del panel: 2 columnas y alto fijo dentro del viewport */}
                  <div className="flex-1 overflow-hidden p-4 sm:p-6">
                    <div className="h-full grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Columna 1: preview ocupa vertical completo (scroll interno solo si el HTML lo excede) */}
                      <div className="flex flex-col min-h-0">
                        <div className="text-xs font-semibold text-white/70 mb-2">Previsualización</div>
                        <div ref={previewHostRef} className="flex-1 min-h-0">
                          {!creativeId ? (
                            <div className="text-sm text-white/60">Este anuncio no tiene `creative_id`.</div>
                          ) : !creativePreview || creativePreview?.isLoading ? (
                            <div className="flex items-center gap-2 text-sm text-white/70">
                              <Loader2 className="w-4 h-4 animate-spin text-white/70" aria-hidden="true" />
                              <span>Cargando contenido</span>
                            </div>
                          ) : creativePreview?.error ? (
                            <div className="text-sm text-rose-300">No se pudo cargar el preview.</div>
                          ) : creativePreview?.html == null ? (
                            <div className="text-sm text-white/60">Sin preview disponible.</div>
                          ) : (
                            <div className="relative h-full overflow-hidden rounded-xl border border-white/10 flex items-start justify-center">
                              {(() => {
                                const w = previewHostSize.w || 0;
                                const h = previewHostSize.h || 0;
                                const cw = previewContentSize.w || 0;
                                const ch = previewContentSize.h || 0;
                                const BASE_W = cw || 360;
                                const BASE_H = ch || 760;
                                const scaleW = w > 0 ? w / BASE_W : 1;
                                const scaleH = h > 0 ? h / BASE_H : 1;
                                const scale = Math.max(0.25, Math.min(scaleW, scaleH, 1));
                                const scaledW = BASE_W * scale;
                                const scaledH = BASE_H * scale;

                                return (
                                  <>
                                    {/* Nodo de medición (sin transform) para no contaminar scrollHeight */}
                                    <div
                                      ref={previewContentRef}
                                      dangerouslySetInnerHTML={{ __html: creativePreview.html }}
                                      style={{
                                        position: 'absolute',
                                        visibility: 'hidden',
                                        pointerEvents: 'none',
                                        width: 360,
                                        zIndex: -1,
                                      }}
                                    />

                                    {/* Visible: usar zoom (layout-aware) con fallback a transform */}
                                    {supportsZoom ? (
                                      <div
                                        style={{
                                          width: BASE_W,
                                          height: BASE_H,
                                          zoom: scale,
                                          flexShrink: 0,
                                        }}
                                        className="rounded-xl overflow-hidden bg-transparent"
                                        dangerouslySetInnerHTML={{ __html: creativePreview.html }}
                                      />
                                    ) : (
                                      <div style={{ width: scaledW, height: scaledH, overflow: 'hidden', flexShrink: 0 }}>
                                        <div
                                          style={{
                                            transform: `scale(${scale})`,
                                            transformOrigin: 'top left',
                                            width: BASE_W,
                                            height: BASE_H,
                                          }}
                                          className="rounded-xl overflow-hidden bg-transparent"
                                          dangerouslySetInnerHTML={{ __html: creativePreview.html }}
                                        />
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Columna 2: tendencia + CTAs + leyenda */}
                      <div className="flex flex-col gap-4 min-h-0">
                        <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
                          <div className="text-xs font-semibold text-white/70 mb-2">
                            Tendencia {objectiveLabel} <span className="text-white/50 font-normal">(últimas 4 semanas)</span>
                          </div>
                          <div className="h-[220px] w-full">
                            {trendData.length === 0 ? (
                              <div className="text-sm text-white/60">Sin datos de tendencia.</div>
                            ) : (
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={trendData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                                  <CartesianGrid stroke="rgba(255,255,255,0.08)" strokeDasharray="3 3" />
                                  <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }} />
                                  <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 12 }} />
                                  <Tooltip
                                    contentStyle={{
                                      background: 'rgba(15, 23, 42, 0.95)',
                                      border: '1px solid rgba(255,255,255,0.15)',
                                      borderRadius: 12,
                                      color: 'white',
                                      fontSize: 12,
                                    }}
                                    labelStyle={{ color: 'rgba(255,255,255,0.7)' }}
                                    formatter={(val) => {
                                      if (val === null || val === undefined) return ['—', objectiveLabel];
                                      const n = Number(val);
                                      if (!Number.isFinite(n)) return ['—', objectiveLabel];
                                      return [objective === 'cpa' ? formatMoney2(n) : formatNumber2(n), objectiveLabel];
                                    }}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="value"
                                    stroke="rgba(217, 70, 239, 0.95)"
                                    strokeWidth={2}
                                    dot={{ r: 3, fill: 'rgba(217, 70, 239, 0.95)' }}
                                    connectNulls={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>

                        <div className="rounded-2xl border border-white/15 bg-white/5 p-3">
                          <div className="text-xs font-semibold text-white/70 mb-2">¿Por qué esta etiqueta?</div>
                          {(() => {
                            const ui = getLabelUi(selectedAd.label);
                            if (!ui) return <p className="text-sm text-white/60">Sin etiqueta.</p>;
                            return (
                              <div className="space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold tracking-wide ${ui.pillClass}`}>
                                    {ui.label}
                                  </span>
                                  <span className="text-xs text-white/50">Basado en performance reciente</span>
                                </div>
                                <p className="text-sm text-white/75 leading-relaxed">{ui.description}</p>
                              </div>
                            );
                          })()}
                        </div>

                        {/* CTAs: sin recuadro, ocupan el espacio sobrante */}
                        <div className="flex flex-col flex-1 min-h-0">
                          <div className="text-xs font-semibold text-white/70 mb-2">Acciones</div>
                          <div className="grid grid-cols-1 gap-2 flex-1 content-start">
                            <button
                              type="button"
                              disabled={isApplyingAction(selectedAd?.ad_id, 'BUDGET_UP')}
                              onClick={async () => {
                                const adId = selectedAd?.ad_id;
                                if (!adId) return;
                                const ctx = budgetContextByAdId?.[adId]?.data || await refreshBudgetContext(adId);
                                const resolved = ctx?.resolved;
                                const currentBudget = resolved?.current_budget ?? ctx?.current_budget ?? ctx?.before?.budget_value ?? null;
                                const base = currentBudget != null ? Number(currentBudget) : null;
                                const suggested = base != null && Number.isFinite(base) ? Math.round(base * 1.2) : '';
                                setBudgetActionDraft({ ad_id: adId, direction: 'up', mode: 'ABSOLUTE', value: suggested });
                              }}
                              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-400/25 bg-emerald-500/10 hover:bg-emerald-500/15 text-emerald-100 px-3 py-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40"
                            >
                              <TrendingUp className="h-4 w-4" />
                              {isApplyingAction(selectedAd?.ad_id, 'BUDGET_UP') ? 'Aplicando…' : 'Aumentar presupuesto'}
                            </button>
                            <button
                              type="button"
                              disabled={isApplyingAction(selectedAd?.ad_id, 'PAUSE')}
                              onClick={() => {
                                const adId = selectedAd?.ad_id;
                                if (!adId) return;
                                setPauseConfirmAdId(adId);
                              }}
                              className="inline-flex items-center justify-center gap-2 rounded-xl border border-amber-400/25 bg-amber-500/10 hover:bg-amber-500/15 text-amber-100 px-3 py-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40"
                            >
                              <PauseCircle className="h-4 w-4" />
                              {isApplyingAction(selectedAd?.ad_id, 'PAUSE') ? 'Aplicando…' : 'Pausar anuncio'}
                            </button>
                            <button
                              type="button"
                              disabled={isApplyingAction(selectedAd?.ad_id, 'BUDGET_DOWN')}
                              onClick={async () => {
                                const adId = selectedAd?.ad_id;
                                if (!adId) return;
                                const ctx = budgetContextByAdId?.[adId]?.data || await refreshBudgetContext(adId);
                                const resolved = ctx?.resolved;
                                const currentBudget = resolved?.current_budget ?? ctx?.current_budget ?? ctx?.before?.budget_value ?? null;
                                const base = currentBudget != null ? Number(currentBudget) : null;
                                const suggested = base != null && Number.isFinite(base) ? Math.max(1, Math.round(base * 0.8)) : '';
                                setBudgetActionDraft({ ad_id: adId, direction: 'down', mode: 'ABSOLUTE', value: suggested });
                              }}
                              className="inline-flex items-center justify-center gap-2 rounded-xl border border-rose-400/25 bg-rose-500/10 hover:bg-rose-500/15 text-rose-100 px-3 py-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/40"
                            >
                              <TrendingDown className="h-4 w-4" />
                              {isApplyingAction(selectedAd?.ad_id, 'BUDGET_DOWN') ? 'Aplicando…' : 'Disminuir presupuesto'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Popups centrados */}
              {pauseConfirmAdId === selectedAd?.ad_id ? (
                <div className="fixed inset-0 z-[10050]" role="dialog" aria-modal="true" aria-label="Confirmar pausa">
                  <button
                    type="button"
                    aria-label="Cerrar confirmación"
                    onClick={() => setPauseConfirmAdId(null)}
                    className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
                  />
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="w-full max-w-md rounded-2xl border border-white/20 bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 shadow-2xl p-4 sm:p-5 text-white/90">
                      <div className="text-white font-semibold">Pausar anuncio</div>
                      <div className="text-sm text-white/70 mt-1">
                        Vas a pausar este anuncio en Meta.
                      </div>
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setPauseConfirmAdId(null)}
                          className="rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-white/80 px-3 py-2 text-sm font-semibold"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            const adId = selectedAd?.ad_id;
                            if (!adId) return;
                            setPauseConfirmAdId(null);
                            await executeQuickAction(
                              adId,
                              {
                                account_id: accountId.trim(),
                                ad_id: adId,
                                action: 'PAUSE',
                                dry_run: false,
                              },
                              { successMessage: 'Anuncio pausado.', actionKey: 'PAUSE' }
                            );
                          }}
                          className="rounded-xl border border-amber-400/25 bg-amber-500/20 hover:bg-amber-500/25 text-amber-50 px-3 py-2 text-sm font-semibold"
                        >
                          Confirmar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {budgetActionDraft && budgetActionDraft.ad_id === selectedAd?.ad_id ? (
                <div className="fixed inset-0 z-[10050]" role="dialog" aria-modal="true" aria-label="Modificar presupuesto">
                  <button
                    type="button"
                    aria-label="Cerrar presupuesto"
                    onClick={() => setBudgetActionDraft(null)}
                    className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
                  />
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="w-full max-w-lg rounded-2xl border border-white/20 bg-gradient-to-br from-slate-800/95 via-slate-900/95 to-indigo-900/95 shadow-2xl p-4 sm:p-5 text-white/90">
                      {(() => {
                        const adId = selectedAd?.ad_id;
                        const ctx = adId ? budgetContextByAdId?.[adId] : null;
                        const data = ctx?.data;
                        const resolved = data?.resolved;
                        const beforeBudget =
                          resolved?.current_budget ?? data?.current_budget ?? data?.before?.budget_value ?? null;
                        const currency = resolved?.currency || '—';
                        const level = resolved?.budget_level || '—';
                        const type = resolved?.budget_type || '—';
                        const entityLabel = (() => {
                          const campaignId = resolved?.campaign_id || data?.campaign_id || null;
                          const adsetId = resolved?.adset_id || data?.adset_id || null;
                          const campaignName = resolved?.campaign_name || null;
                          const adsetName = resolved?.adset_name || null;
                          if (level === 'CAMPAIGN') return campaignName || (campaignId ? `Campaña ${campaignId}` : 'Campaña');
                          if (level === 'ADSET') return adsetName || (adsetId ? `Ad set ${adsetId}` : 'Ad set');
                          return campaignName || adsetName || 'Entidad';
                        })();
                        const periodLabel = type === 'LIFETIME' ? 'Total' : 'Diario';
                        return (
                          <>
                            <div className="text-sm font-semibold text-white/90">
                              Presupuesto — {entityLabel} — {periodLabel}
                            </div>

                            {/* Actual */}
                            <div className="mt-3 rounded-2xl border border-white/15 bg-white/5 p-3">
                              <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-semibold text-white/90">Actual</div>
                                <div className="flex-1 max-w-[340px]">
                                  {ctx?.isLoading ? (
                                    <div className="h-11 flex items-center gap-2 text-sm text-white/70">
                                      <Loader2 className="w-4 h-4 animate-spin text-white/70" aria-hidden="true" />
                                      <span>Resolviendo…</span>
                                    </div>
                                  ) : ctx?.error ? (
                                    <div className="h-11 flex items-center text-sm text-rose-200/80">
                                      {ctx.error}
                                    </div>
                                  ) : (
                                    <div className="flex items-stretch rounded-xl border border-white/15 bg-black/20 overflow-hidden">
                                      <div
                                        className="w-full h-11 bg-transparent px-3 text-base text-white/90 font-mono flex items-center select-none cursor-not-allowed"
                                        aria-label="Presupuesto actual"
                                        title="Presupuesto actual (solo lectura)"
                                      >
                                        {beforeBudget == null
                                          ? '—'
                                          : `${Number(beforeBudget).toLocaleString('es-AR', { maximumFractionDigits: 2 })}`}
                                      </div>
                                      <div className="flex items-center px-3 text-sm font-semibold text-white/80 border-l border-white/10 bg-white/5 select-none">
                                        {currency}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Input estilo Meta: Nuevo + monto + moneda */}
                            <div className="mt-3 rounded-2xl border border-white/15 bg-white/5 p-3">
                              <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-semibold text-white/90">Nuevo</div>
                                <div className="flex-1 max-w-[340px]">
                                  <div className="flex items-stretch rounded-xl border border-white/15 bg-black/20 overflow-hidden focus-within:ring-2 focus-within:ring-fuchsia-500/30">
                                    <input
                                      id="budget-absolute"
                                      inputMode="decimal"
                                      type="number"
                                      min="1"
                                      step="1"
                                      value={budgetActionDraft.value}
                                      onChange={(e) =>
                                        setBudgetActionDraft((prev) => ({ ...(prev || {}), value: e.target.value }))
                                      }
                                      className="w-full h-11 bg-transparent px-3 text-base text-white placeholder:text-white/30 focus:outline-none"
                                      placeholder="201"
                                      aria-label="Nuevo presupuesto"
                                    />
                                    <div className="flex items-center px-3 text-sm font-semibold text-white/80 border-l border-white/10 bg-white/5">
                                      {currency}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* Nota removida (pedida): "Nuevo presupuesto" */}
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => setBudgetActionDraft(null)}
                                className="rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-white/80 px-3 py-2 text-sm font-semibold"
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                onClick={async () => {
                                  const adId2 = selectedAd?.ad_id;
                                  if (!adId2) return;
                                  const n = Number(budgetActionDraft.value);
                                  if (!Number.isFinite(n) || n <= 0) {
                                    toast.error('Ingresá un presupuesto válido (> 0).');
                                    return;
                                  }
                                  const actionKey = budgetActionDraft.direction === 'up' ? 'BUDGET_UP' : 'BUDGET_DOWN';
                                  setBudgetActionDraft(null);
                                  await executeQuickAction(
                                    adId2,
                                    {
                                      account_id: accountId.trim(),
                                      ad_id: adId2,
                                      action: 'SET_BUDGET',
                                      budget: { mode: 'ABSOLUTE', value: n },
                                      budget_hint: { preferred_level: 'AUTO' },
                                      dry_run: false,
                                    },
                                    { successMessage: 'Presupuesto actualizado.', actionKey }
                                  );
                                }}
                                className="rounded-xl border border-fuchsia-400/25 bg-fuchsia-500/15 hover:bg-fuchsia-500/20 text-fuchsia-50 px-3 py-2 text-sm font-semibold"
                              >
                                Publicar
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })()}

        <Link to="/home" className="inline-block text-sm text-fuchsia-400/90 hover:underline">
          Volver a home
        </Link>
      </div>
    </div>
  );
};

export default MetaCreativeRankingPlayground;
